import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';

export interface Column<T> {
  key: string;
  label: React.ReactNode;
  render: (row: T) => React.ReactNode;
  sortValue?: (row: T) => string | number;
  align?: 'left' | 'right' | 'center';
  width?: number | string;
  hideBelow?: 'sm' | 'md' | 'lg' | 'xl';
}

interface DataTableProps<T> {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  pageSize?: number;
  dense?: boolean;
  defaultSort?: { key: string; direction: 'asc' | 'desc' };
  footer?: React.ReactNode;
  selectable?: boolean;
  selected?: Set<string>;
  onSelectedChange?: (selected: Set<string>) => void;
  rowSx?: (row: T) => object | undefined;
}

export default function DataTable<T>({
  rows,
  columns,
  rowKey,
  onRowClick,
  emptyMessage = 'Nothing to show',
  pageSize = 25,
  dense,
  defaultSort,
  footer,
  selectable,
  selected,
  onSelectedChange,
  rowSx,
}: DataTableProps<T>) {
  const [sort, setSort] = useState(defaultSort);
  const [page, setPage] = useState(0);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const column = columns.find((item) => item.key === sort.key);
    if (!column?.sortValue) return rows;
    const pick = column.sortValue;
    return [...rows].sort((left, right) => {
      const a = pick(left);
      const b = pick(right);
      const result =
        typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b));
      return sort.direction === 'asc' ? result : -result;
    });
  }, [rows, columns, sort]);

  const paged =
    sorted.length > pageSize ? sorted.slice(page * pageSize, page * pageSize + pageSize) : sorted;
  const hide = (column: Column<T>) =>
    column.hideBelow ? { display: { xs: 'none', [column.hideBelow]: 'table-cell' } } : undefined;
  const allSelected =
    selectable && rows.length > 0 && rows.every((row) => selected?.has(rowKey(row)));

  const toggle = (key: string) => {
    if (!onSelectedChange) return;
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onSelectedChange(next);
  };

  return (
    <Box>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size={dense ? 'small' : 'medium'} sx={{ minWidth: 560 }}>
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={Boolean(allSelected)}
                    indeterminate={!allSelected && (selected?.size ?? 0) > 0}
                    onChange={() =>
                      onSelectedChange?.(allSelected ? new Set() : new Set(rows.map(rowKey)))
                    }
                    inputProps={{ 'aria-label': 'Select all rows' }}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align}
                  sx={{
                    width: column.width,
                    color: 'text.secondary',
                    whiteSpace: 'nowrap',
                    ...hide(column),
                  }}
                >
                  {column.sortValue ? (
                    <TableSortLabel
                      active={sort?.key === column.key}
                      direction={sort?.key === column.key ? sort.direction : 'asc'}
                      onClick={() =>
                        setSort((current) => ({
                          key: column.key,
                          direction:
                            current?.key === column.key && current.direction === 'desc'
                              ? 'asc'
                              : 'desc',
                        }))
                      }
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length + (selectable ? 1 : 0)}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 3, textAlign: 'center' }}
                  >
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {paged.map((row) => {
              const key = rowKey(row);
              return (
                <TableRow
                  key={key}
                  hover={Boolean(onRowClick)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={
                    onRowClick
                      ? (event) => {
                          if (event.key === 'Enter' && event.target === event.currentTarget)
                            onRowClick(row);
                        }
                      : undefined
                  }
                  sx={{
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: -2,
                    },
                    cursor: onRowClick ? 'pointer' : 'default',
                    '&:last-child td': { borderBottom: footer ? undefined : 0 },
                    ...rowSx?.(row),
                  }}
                >
                  {selectable && (
                    <TableCell padding="checkbox" onClick={(event) => event.stopPropagation()}>
                      <Checkbox
                        size="small"
                        checked={selected?.has(key) ?? false}
                        onChange={() => toggle(key)}
                        inputProps={{ 'aria-label': 'Select row' }}
                      />
                    </TableCell>
                  )}
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      align={column.align}
                      sx={{ fontVariantNumeric: 'tabular-nums', ...hide(column) }}
                    >
                      {column.render(row)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
          {footer && <TableFooter>{footer}</TableFooter>}
        </Table>
      </TableContainer>
      {sorted.length > pageSize && (
        <TablePagination
          component="div"
          count={sorted.length}
          page={page}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[pageSize]}
          onPageChange={(_event, next) => setPage(next)}
        />
      )}
    </Box>
  );
}
